import { TransformNode as BABYLON_TransformNode } from '@inter-ikea-betrakta-babylonjs/core/Meshes/transformNode.js';
import { Observable } from '../../Observable.js';
import { IMatrix4 } from '../../api.js';
import { SceneAssetInstanceMetadata } from './SceneAssetMetadata.js';
import { matrix_pool } from '../../Math.js';
import { Logger } from '../../logging/Logger.js';
const LOGGER_CATEGORY = 'SceneAssetInstance';
export class SceneAssetInstance {
    constructor(scene, mesh, model_transform) {
        this._mesh_instances = [];
        this.meshes_updated_observable = new Observable();
        this.disposed_observable = new Observable();
        this._override_material = null;
        this.metadata = new SceneAssetInstanceMetadata();
        this._receive_shadows = true;
        this.animation_groups = null;
        this.scene = scene;
        this.asset = mesh;
        this.root = new BABYLON_TransformNode('asset instance root', this.scene.babylon_scene);
        if (model_transform !== null) {
            this._model_transform = IMatrix4.copy(model_transform);
            const babylon_model_transform = matrix_pool.fetch().fromIMatrix4(model_transform);
            babylon_model_transform.decomposeToTransformNode(this.root);
        }
        else {
            this._model_transform = null;
        }
        this.root.freezeWorldMatrix();
    }
    on_new_meshes(mesh_instances) {
        this._mesh_instances.length = mesh_instances.length;
        for (let i = 0; i < this._mesh_instances.length; i++) {
            this._mesh_instances[i] = mesh_instances[i];
            mesh_instances[i].set_receive_shadows(this._receive_shadows);
            mesh_instances[i].mark_variant_key_dirty();
        }
        this.meshes_updated_observable.notify(this, { new_meshes: mesh_instances });
    }
    mesh_iterator(func) {
        for (let i = 0; i < this._mesh_instances.length; i++) {
            func(this._mesh_instances[i].babylon_instance);
        }
    }
    instance_iterator(func) {
        this._mesh_instances.forEach(func);
    }
    set_parent(parent) {
        this.root.parent = parent;
        this.root.markAsDirty();
    }
    dispose_mesh() {
        this._mesh_instances.forEach(mesh => mesh.dispose());
        this._mesh_instances.length = 0;
        this.root.getChildren(undefined, true).forEach((child) => {
            child.dispose(false);
        });
    }
    dispose() {
        this.dispose_mesh();
        this.asset.instance_disposed(this);
        this.disposed_observable.notify(this);
        this.meshes_updated_observable.dispose();
        this.disposed_observable.dispose();
    }
    set_override_material(material) {
        if (this._override_material === material)
            return;
        this._override_material = material;
        this._mesh_instances.forEach((mesh) => {
            mesh.mark_variant_key_dirty();
        });
    }
    get_override_material() {
        return this._override_material;
    }
    set_receive_shadows(value) {
        if (value === this._receive_shadows)
            return;
        this._receive_shadows = value;
        this._mesh_instances.forEach(mesh => mesh.set_receive_shadows(value));
    }
    prepare_for_render() {
        for (let i = 0; i < this._mesh_instances.length; i++) {
            this._mesh_instances[i].prepare_for_render();
        }
    }
    clone() {
        const clone = this.asset.create_instance(this._model_transform);
        clone.set_override_material(this._override_material);
        // Copy properties on the individual instances
        // This assumes the same ordering, TODO improve?
        if (this._mesh_instances.length !== clone._mesh_instances.length) {
            Logger.as(LOGGER_CATEGORY).warn("Invalid state: Clone's instance count does not match the source's instance count");
        }
        else {
            let has_warned_for_invalid_state = false;
            for (let i = 0; i < clone._mesh_instances.length; i++) {
                if (clone._mesh_instances[i].source === this._mesh_instances[i].source) {
                    clone._mesh_instances[i].copy_settings_from(this._mesh_instances[i]);
                }
                else {
                    if (!has_warned_for_invalid_state) {
                        Logger.as(LOGGER_CATEGORY).warn("Invalid state: Clone's instance's source does not match source's instance source");
                        has_warned_for_invalid_state = true;
                    }
                }
            }
        }
        return clone;
    }
}
//# sourceMappingURL=SceneAssetInstance.js.map